import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, Download } from 'lucide-react';
import type { ResultRegionWiseItem } from '../services/api';

interface Props {
  data: ResultRegionWiseItem[];
  activeTab: string;
  year: number;
}

type SortKey = keyof ResultRegionWiseItem;

const RegionWiseTable: React.FC<Props> = ({ data, activeTab, year }) => {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>('passPercentage');
  const [sortAsc, setSortAsc] = useState(false);
  const [search, setSearch] = useState('');
  const [topBottomMode, setTopBottomMode] = useState<'none' | 'top' | 'bottom'>('none');
  const [topBottomN, setTopBottomN] = useState('10');
  const [passMin, setPassMin] = useState('');
  const [passMax, setPassMax] = useState('');
  const [piMin, setPiMin]     = useState('');
  const [piMax, setPiMax]     = useState('');

  const avgPassPct = useMemo(
    () => (data.length ? data.reduce((s, r) => s + r.passPercentage, 0) / data.length : 0),
    [data],
  );
  const avgPerfIndex = useMemo(
    () => (data.length ? data.reduce((s, r) => s + r.performanceIndex, 0) / data.length : 0),
    [data],
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  // When primary sort is PI or Pass%, the other automatically becomes the secondary sort (same direction)
  const linkedSecKey: SortKey | null =
    sortKey === 'performanceIndex' ? 'passPercentage' :
    sortKey === 'passPercentage'   ? 'performanceIndex' : null;

  // 1. Search + sort
  const sortedFiltered = data
    .filter((r) => r.region.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') cmp = sortAsc ? av - bv : bv - av;
      else cmp = sortAsc ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      if (cmp !== 0) return cmp;
      if (linkedSecKey) {
        const as2 = a[linkedSecKey], bs2 = b[linkedSecKey];
        if (typeof as2 === 'number' && typeof bs2 === 'number') return sortAsc ? as2 - bs2 : bs2 - as2;
      }
      return 0;
    });

  // 2. Pass% and PI range filters
  const rangeFiltered = sortedFiltered.filter((r) => {
    const pm = passMin !== '' ? r.passPercentage   >= Number(passMin) : true;
    const px = passMax !== '' ? r.passPercentage   <= Number(passMax) : true;
    const im = piMin   !== '' ? r.performanceIndex >= Number(piMin)   : true;
    const ix = piMax   !== '' ? r.performanceIndex <= Number(piMax)   : true;
    return pm && px && im && ix;
  });

  // 3. Top / Bottom N slice
  const n = Math.max(1, parseInt(topBottomN, 10) || 10);
  const displayRows = topBottomMode === 'top'    ? rangeFiltered.slice(0, n)
                    : topBottomMode === 'bottom' ? rangeFiltered.slice(-n)
                    : rangeFiltered;

  function downloadCSV() {
    const headers = ['#', 'Region', 'Registered', 'Appeared', 'Passed', 'Compartment', 'Absent', 'Ess. Repeat', 'Pass%', 'PI'];
    const rows = displayRows.map((r, i) => [
      i + 1, r.region, r.totalRegistered ?? 0, r.totalAppeared ?? 0, r.passed ?? 0,
      r.compartment ?? 0, r.absent ?? 0, r.totalEssentialRepeat ?? 0, r.passPercentage, r.performanceIndex,
    ].map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(','));
    const csv  = [headers.map((h) => '"' + h + '"').join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'region-performance.csv';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  const ThBtn: React.FC<{ label: string; field: SortKey }> = ({ label, field }) => {
    const isPrimary = sortKey === field;
    const isSecondary = linkedSecKey === field;
    const SortIcon = isPrimary || isSecondary
      ? (sortAsc ? ArrowUp : ArrowDown)
      : ArrowUpDown;
    const iconClass = isPrimary
      ? 'w-3 h-3 text-purple-600'
      : isSecondary
        ? 'w-3 h-3 text-purple-300'
        : 'w-3 h-3 text-gray-400';
    return (
      <th
        className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap cursor-pointer select-none hover:bg-gray-100 transition-colors"
        onClick={() => handleSort(field)}
        title={isSecondary ? 'Co-sorted with ' + (field === 'passPercentage' ? 'PI' : 'Pass%') : undefined}
      >
        <span className="flex items-center gap-1">
          {label}
          <SortIcon className={iconClass} />
        </span>
      </th>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 gap-3">
        <h3 className="text-lg font-semibold text-gray-800">Region-wise Performance</h3>
        <div className="relative w-full md:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" placeholder="Search region…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300" />
        </div>
      </div>

      {/* Filter bar: Top/Bottom N · Pass% range · PI range · CSV */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-4 py-2.5 px-3 bg-gray-50 rounded-lg border border-gray-100 text-xs">
        <div className="flex items-center gap-1.5">
          <span className="text-gray-500 font-medium">Show:</span>
          <div className="flex rounded overflow-hidden border border-gray-200">
            {(['none', 'top', 'bottom'] as const).map((m) => (
              <button key={m} onClick={() => setTopBottomMode(m)}
                className={'px-2 py-1 transition-colors ' + (topBottomMode === m ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}>
                {m === 'none' ? 'All' : m === 'top' ? 'Top' : 'Bottom'}
              </button>
            ))}
          </div>
          <input type="number" min={1} value={topBottomN} onChange={(e) => setTopBottomN(e.target.value)}
            disabled={topBottomMode === 'none'}
            className="w-12 px-1.5 py-1 rounded border border-gray-200 text-center disabled:opacity-40 focus:outline-none focus:ring-1 focus:ring-purple-300" />
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <div className="flex items-center gap-1">
          <span className="text-gray-500 font-medium">Pass%:</span>
          <input type="number" placeholder="min" value={passMin} onChange={(e) => setPassMin(e.target.value)}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-purple-300" />
          <span className="text-gray-400">–</span>
          <input type="number" placeholder="max" value={passMax} onChange={(e) => setPassMax(e.target.value)}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-purple-300" />
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <div className="flex items-center gap-1">
          <span className="text-gray-500 font-medium">PI:</span>
          <input type="number" placeholder="min" value={piMin} onChange={(e) => setPiMin(e.target.value)}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-purple-300" />
          <span className="text-gray-400">–</span>
          <input type="number" placeholder="max" value={piMax} onChange={(e) => setPiMax(e.target.value)}
            className="w-14 px-1.5 py-1 rounded border border-gray-200 text-center focus:outline-none focus:ring-1 focus:ring-purple-300" />
        </div>
        <div className="flex-1" />
        <span className="text-gray-400">{displayRows.length} row{displayRows.length !== 1 ? 's' : ''}</span>
        <button onClick={downloadCSV}
          className="flex items-center gap-1 px-2.5 py-1 rounded bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors">
          <Download className="w-3 h-3" />CSV
        </button>
      </div>

      <div className="overflow-auto max-h-[520px]">
        <table className="w-full text-left">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="py-3 px-4 text-sm font-semibold text-gray-600 whitespace-nowrap">#</th>
              <ThBtn label="Region" field="region" />
              <ThBtn label="Registered" field="totalRegistered" />
              <ThBtn label="Appeared" field="totalAppeared" />
              <ThBtn label="Passed" field="passed" />
              <ThBtn label="Compartment" field="compartment" />
              <ThBtn label="Absent" field="absent" />
              <ThBtn label="Ess. Repeat" field="totalEssentialRepeat" />
              <ThBtn label={`Pass % (avg ${avgPassPct.toFixed(1)}%)`} field="passPercentage" />
              <ThBtn label={`Perf. Index (avg ${avgPerfIndex.toFixed(2)})`} field="performanceIndex" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {displayRows.map((row, i) => (
              <tr
                key={row.region}
                className="hover:bg-orange-50 transition-colors cursor-pointer"
                onClick={() => navigate(`region/${encodeURIComponent(row.region)}?class=${activeTab}&year=${year}`)}
              >
                <td className="py-3 px-4 text-sm text-gray-500">{i + 1}</td>
                <td className="py-3 px-4 text-sm font-medium text-gray-800">{row.region}</td>
                <td className="py-3 px-4 text-sm text-gray-700">{(row.totalRegistered ?? 0).toLocaleString()}</td>
                <td className="py-3 px-4 text-sm text-gray-700">{(row.totalAppeared ?? 0).toLocaleString()}</td>
                <td className="py-3 px-4 text-sm text-green-700 font-medium">{(row.passed ?? 0).toLocaleString()}</td>
                <td className="py-3 px-4 text-sm text-orange-600">{row.compartment ?? 0}</td>
                <td className="py-3 px-4 text-sm text-blue-600">{row.absent ?? 0}</td>
                <td className="py-3 px-4 text-sm text-red-600">{row.totalEssentialRepeat ?? 0}</td>
                <td className="py-3 px-4 text-sm">
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                    row.passPercentage >= avgPassPct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {row.passPercentage}%
                  </span>
                </td>
                <td className="py-3 px-4 text-sm font-medium">
                  <span className={row.performanceIndex >= avgPerfIndex ? 'text-green-700' : 'text-red-600'}>
                    {row.performanceIndex}
                  </span>
                </td>
              </tr>
            ))}
            {displayRows.length === 0 && (
              <tr>
                <td colSpan={10} className="py-8 text-center text-gray-400">No regions found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RegionWiseTable;
